import * as React from 'react'

import { type Params as StaticParams, path as staticPath } from 'static-path'

import {
  BuiltLocation,
  LocationBuilder,
  PathExtractor,
  SafeParams,
  SafePath,
  navigationContext,
} from './context'
import { navigation } from './index'
import type { Location } from './types'

// This removes first and last slash from the path, if some
const sanitizePath = (path: string) => {
  if (path === '/') return path

  return path.replace(/^\/|\/$/g, '')
}

const buildPath = <T extends Location>(rawPath: string, parentPath?: T['path']) => {
  const path = sanitizePath(rawPath)

  return !parentPath ? path : (`${parentPath}/${path}` as PathExtractor<T, T['path']>)
}

const buildStaticPath = <Pattern extends string>(pattern: Pattern): SafePath<Pattern> => {
  const path = staticPath(pattern)

  return (params: SafeParams<Pattern>) => {
    const sanitizedParams = Object.entries(params ?? {}).reduce(
      (prev, [key, value]) => ({
        ...prev,
        [key]: String(value),
      }),
      {}
    ) as StaticParams<Pattern>

    return path(sanitizedParams)
  }
}

const buildChildren = <T extends Location>(
  children: Location['children'],
  currentPath: T['path']
): Record<string, BuiltLocation<Location, string>> => {
  if (!children) return {}

  return Object.keys(children).reduce((memo, key) => {
    const child = children[key]

    if (child) return { ...memo, [key]: buildNavigation(child, currentPath) }

    return memo
  }, {})
}

const buildNavigation = <T extends Location>(location: T, parentPath?: T['path']) => {
  const { children, path, ...rest } = location
  const currentPath = buildPath(path, parentPath)

  return {
    ...rest,
    path,
    fullPath: buildStaticPath(currentPath),
    ...buildChildren(children, currentPath),
  } as unknown as LocationBuilder<T, T['path']>
}

export const NavigationProvider = ({ children }: React.PropsWithChildren) => {
  const { Provider } = navigationContext
  const builtNavigation = buildNavigation(navigation)

  return (
    <Provider
      value={{
        navigation: builtNavigation,
      }}
    >
      {children}
    </Provider>
  )
}
